import React, { useState } from 'react'
import { App as AntdApp, Input, Modal, Typography } from 'antd'
import { useAuth } from '@/contexts/AuthContext'

const { Text } = Typography

/**
 * 세션(JWT)이 만료됐을 때 **페이지를 떠나지 않고** 그 자리에서 재인증한다(2026-09-22, 사용자 요청 — "1순위").
 *
 * 예전에는 axiosClient가 401을 맞으면 곧바로 `/login`으로 전체 페이지를 이동시켰다. 그러면 기록입력
 * 화면(RecordInputPage)이 통째로 언마운트되면서 입력 중이던 값이 끊기고, 무엇보다 배경에서 돌던 오프라인
 * 큐 재전송(useOfflineSync)까지 멈춰서 **이미 "저장"을 눌러 로컬 큐에 들어간 투구조차** 재로그인 후 그
 * 경기를 다시 찾아 들어가기 전까지 서버로 못 갔다.
 *
 * 이제는 `AuthContext.sessionExpired` 신호만 받아 비밀번호만 다시 받고, 성공하면 화면은 그대로 둔 채
 * 토큰만 교체한다 — 이미 돌고 있던 재전송 루프가 새 토큰으로 바로 이어서 큐를 비운다.
 *
 * `App.tsx`에서 라우트와 무관하게 **한 번만** 마운트한다 — `/recorder/:gameInfoId`는 MainLayout
 * 바깥의 전체화면 라우트라 레이아웃 안에 두면 그 화면에서는 못 뜬다.
 *
 * cookie 모드도 같은 비밀번호 재인증을 쓴다. 이 모달이 뜨기 전에 AuthContext가 쿠키 로그인(쿠키의 이 앱·관리 화면
 * 토큰 검증)을 먼저 조용히 시도하므로, 여기까지 왔다면 쿠키로는 이어갈 수 없는 상태다. 예전에는 cookie 모드에서
 * 외부 로그인 페이지로 보냈는데, 그러면 화면을 떠나 입력 중이던 값이 끊기고 외부 로그인으로는 이 앱이 검증할 수
 * 있는 토큰도 생기지 않는다.
 */
const SessionExpiredModal: React.FC = () => {
  const { message } = AntdApp.useApp()
  const { user, sessionExpired, reauthenticate, logout } = useAuth()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  // 로그인 자체가 안 된 상태(예: 앱을 열었을 때 이미 만료된 토큰만 남아 있던 경우)에서는 되물을
  // 사용자 정보가 없다 — PrivateRoute가 알아서 /login으로 보내므로 이 모달은 나서지 않는다.
  if (!sessionExpired || !user) return null

  const handleSubmit = async () => {
    if (!password) return
    setLoading(true)
    try {
      await reauthenticate(password)
      setPassword('')
      message.success('다시 연결됐습니다 — 입력하던 내용 그대로 이어서 진행하십시오.')
    } catch (e) {
      message.error(e instanceof Error ? e.message : '비밀번호가 올바르지 않습니다.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open
      title="세션이 만료됐습니다"
      closable={false}
      maskClosable={false}
      keyboard={false}
      okText="다시 연결"
      cancelText="로그아웃"
      confirmLoading={loading}
      onOk={handleSubmit}
      onCancel={() => { void logout() }}
    >
      <Text style={{ display: 'block', marginBottom: 12 }}>
        {user.name}({user.username})님의 로그인 세션이 만료됐습니다. 비밀번호를 다시 입력하면{' '}
        <b>화면과 입력하던 내용은 그대로 유지</b>되고, 저장 대기 중인 기록도 자동으로 이어서 전송됩니다.
      </Text>
      <Input.Password
        placeholder="비밀번호"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onPressEnter={() => { void handleSubmit() }}
        autoFocus
      />
    </Modal>
  )
}

export default SessionExpiredModal
